export type AppThemeAudience = 'guest' | 'free' | 'paid';

export type AppThemePlatform = 'wcp' | 'webTV' | 'androidTV' | 'appleTV';

export type AppThemeImages = {
    appOverlayEn?: string;
    appOverlayAr?: string;
};

export type AppThemePeriod = {
    startDate?: string;
    endDate?: string;
};

export type AppTheme = {
    id: string;
    activePeriod?: AppThemePeriod;
} & Partial<Record<AppThemePlatform, AppThemeImages>>;

export type AppThemeConfig = {
    userThemes?: Partial<Record<AppThemeAudience, { enable?: boolean; themeId?: string }>>;
    availableThemes?: AppTheme[];
};

export type ResolveAppThemeOptions = {
    audience: AppThemeAudience;
    lang: string;
    platforms?: AppThemePlatform[];
    now?: Date;
};

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const toLocalDate = (value: string | undefined, endOfDay: boolean): Date | null => {
    const match = value ? DATE_ONLY.exec(value) : null;
    if (!match) return null;

    const [year, month, day] = [Number(match[1]), Number(match[2]) - 1, Number(match[3])];
    const date = endOfDay ? new Date(year, month, day, 23, 59, 59, 999) : new Date(year, month, day);

    // rejects rolled-over dates such as 2026-02-30
    return date.getMonth() === month && date.getDate() === day ? date : null;
};

export const isAppThemeActive = (period: AppThemePeriod | undefined, now: Date = new Date()): boolean => {
    const start = toLocalDate(period?.startDate, false);
    const end = toLocalDate(period?.endDate, true);

    return !!start && !!end && now >= start && now <= end;
};

export const resolveAppTheme = (
    themeConfig: AppThemeConfig | undefined,
    { audience, lang, platforms = ['wcp', 'webTV'], now = new Date() }: ResolveAppThemeOptions
): { themeId: string; src: string } | null => {
    const userTheme = themeConfig?.userThemes?.[audience];
    if (!userTheme?.enable || !userTheme.themeId) return null;

    const theme = themeConfig?.availableThemes?.find((item) => item?.id === userTheme.themeId);
    if (!theme || !isAppThemeActive(theme.activePeriod, now)) return null;

    const key = lang === 'ar' ? 'appOverlayAr' : 'appOverlayEn';
    for (const platform of platforms) {
        const src = theme[platform]?.[key]?.trim();
        if (src) return { themeId: theme.id, src };
    }

    return null;
};
